import React, { useEffect, useRef, useState } from "react"
import { View, Text, StyleSheet, Animated, Easing } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"

/**
 * Des étapes qui se cochent une à une, au rythme d'un travail qui se fait.
 *
 * C'est ce qui remplace le spinner aux deux moments « aha » du funnel : la
 * composition du programme et la recherche des masteurs. Une roue qui tourne
 * dit « attendez » ; trois lignes qui se cochent disent CE qu'on fait pour
 * l'élève — et donnent au temps de l'API une forme lisible. La dernière ligne
 * ne se coche que lorsque le travail est réellement terminé (`done`), jamais
 * avant : on n'annonce pas un résultat qu'on n'a pas.
 *
 * Sobre à dessein : un point qui devient une coche, un texte qui s'affirme.
 * Pas de barre, pas de pourcentage.
 */
const STEP_MS = 720
const FIRST_MS = 520

const FunnelStepTicks = ({ lines, done, onFinished, holdMs = 320 }) => {
    const [ticked, setTicked] = useState(0)
    const finishedRef = useRef(false)
    // Le dernier `onFinished` reçu : un re-rendu pendant l'attente de sortie ne
    // doit ni annuler l'appel ni appeler une version périmée.
    const onFinishedRef = useRef(onFinished)
    onFinishedRef.current = onFinished

    // Les lignes intermédiaires se cochent au métronome ; la dernière attend
    // `done`. Un `ticked` qui atteint `lines.length` déclenche la sortie.
    useEffect(() => {
        if (ticked >= lines.length) return undefined
        const isLast = ticked === lines.length - 1
        if (isLast && !done) return undefined
        const t = setTimeout(
            () => setTicked((n) => n + 1),
            ticked === 0 ? FIRST_MS : STEP_MS
        )
        return () => clearTimeout(t)
    }, [ticked, done, lines.length])

    useEffect(() => {
        if (ticked < lines.length || finishedRef.current) return undefined
        finishedRef.current = true
        const t = setTimeout(() => onFinishedRef.current?.(), holdMs)
        return () => clearTimeout(t)
    }, [ticked, lines.length, holdMs])

    return (
        <View style={styles.list}>
            {lines.map((line, i) => (
                <Tick
                    key={line}
                    label={line}
                    state={
                        i < ticked ? "done" : i === ticked ? "active" : "todo"
                    }
                />
            ))}
        </View>
    )
}

const Tick = ({ label, state }) => {
    const pop = useRef(new Animated.Value(0)).current
    // L'étape en cours : un arc qui tourne dans le point — l'attente vit dans
    // l'étape, pas dans une roue posée au milieu de l'écran.
    const spin = useRef(new Animated.Value(0)).current
    useEffect(() => {
        if (state !== "active") return undefined
        const loop = Animated.loop(
            Animated.timing(spin, {
                toValue: 1,
                duration: 900,
                easing: Easing.linear,
                useNativeDriver: true,
            })
        )
        loop.start()
        return () => loop.stop()
    }, [state, spin])
    useEffect(() => {
        if (state !== "done") return
        Animated.timing(pop, {
            toValue: 1,
            duration: 260,
            easing: Easing.out(Easing.back(1.4)),
            useNativeDriver: true,
        }).start()
    }, [state, pop])

    return (
        <View style={styles.row}>
            <View
                style={[
                    styles.dot,
                    state === "done" && styles.dotDone,
                    state === "active" && styles.dotActive,
                ]}
            >
                {state === "active" ? (
                    <Animated.View
                        style={[
                            styles.arc,
                            {
                                transform: [
                                    {
                                        rotate: spin.interpolate({
                                            inputRange: [0, 1],
                                            outputRange: ["0deg", "360deg"],
                                        }),
                                    },
                                ],
                            },
                        ]}
                    />
                ) : null}
                {state === "done" ? (
                    <Animated.View
                        style={{ opacity: pop, transform: [{ scale: pop }] }}
                    >
                        <FontAwesome5
                            name="check"
                            size={9}
                            color={theme.colors.white}
                        />
                    </Animated.View>
                ) : null}
            </View>
            <Text
                style={[
                    styles.label,
                    state === "done" && styles.labelDone,
                    state === "todo" && styles.labelTodo,
                ]}
            >
                {label}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create({
    list: { gap: 14, paddingVertical: 6 },
    row: { flexDirection: "row", alignItems: "center", gap: 12 },
    dot: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        alignItems: "center",
        justifyContent: "center",
    },
    dotActive: { borderColor: theme.colors.primaryLighter },
    arc: {
        position: "absolute",
        top: -2,
        left: -2,
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: theme.colors.primary,
        borderRightColor: "transparent",
        borderBottomColor: "transparent",
    },
    dotDone: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primary,
    },
    label: {
        fontFamily: "Montserrat-Medium",
        fontSize: 14,
        color: theme.colors.primaryDark || theme.colors.primary,
        flex: 1,
    },
    labelDone: { fontFamily: "Montserrat-SemiBold" },
    labelTodo: { color: theme.colors.grey },
})

export default FunnelStepTicks
